import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete
} from '@mui/material';
import { getFilteredRosterForRole } from '../../../utils/wizardHelpers';

// Memoized Autocomplete component to prevent unnecessary re-renders
const MemoizedAutocomplete = React.memo(({ role, itRoster, onRosterMemberChange, readOnly }) => {
  // Memoize the filtered options to prevent recalculation on every render
  const filteredOptions = useMemo(() => {
    return getFilteredRosterForRole(itRoster, role.function, '', role.roster_member_id);
  }, [itRoster, role.function, role.roster_member_id]);

  // Memoize the selected value - find it in the filtered options to ensure consistency
  const selectedValue = useMemo(() => {
    if (!role.roster_member_id) return null;

    // First try to find in filtered options to ensure consistency
    const foundInFiltered = filteredOptions.find(member => member.id === role.roster_member_id);
    if (foundInFiltered) {
      return foundInFiltered;
    }

    // If not found in filtered options, find in full roster (this handles edge cases)
    const foundInRoster = itRoster.find(member => member.id === role.roster_member_id);
    if (foundInRoster) {
      // Log this case for debugging
      console.log(`MemoizedAutocomplete: Member ${foundInRoster.name} (${foundInRoster.function}) not in filtered options for role function '${role.function}'`);
      return foundInRoster;
    }

    return null;
  }, [filteredOptions, itRoster, role.roster_member_id, role.function]);

  // Memoize the filter function
  const filterOptions = useCallback((options, { inputValue }) => {
    return getFilteredRosterForRole(itRoster, role.function, inputValue, role.roster_member_id);
  }, [itRoster, role.function, role.roster_member_id]);

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    const name = option.name || `${option.first_name || ''} ${option.last_name || ''}`.trim();
    return name || 'Unknown';
  }, []);

  const renderOption = useCallback((props, option) => (
    <Box component="li" {...props}>
      <Box>
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {option.name || `${option.first_name || ''} ${option.last_name || ''}`.trim()}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {option.function} - Rate calculated dynamically
        </Typography>
      </Box>
    </Box>
  ), []);

  const renderInput = useCallback((params) => (
    <TextField
      {...params}
      placeholder="Search..."
      variant="outlined"
      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
    />
  ), []);

  const isOptionEqualToValue = useCallback((option, value) => {
    if (!option || !value) return false;
    return option.id === value.id;
  }, []);

  return (
    <Autocomplete
      size="small"
      options={filteredOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      value={selectedValue}
      onChange={(event, newValue) => onRosterMemberChange(newValue)}
      renderInput={renderInput}
      disabled={readOnly}
      filterOptions={filterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      sx={{ minWidth: 180, '& .MuiAutocomplete-input': { fontSize: '0.75rem' } }}
    />
  );
});

MemoizedAutocomplete.displayName = 'MemoizedAutocomplete';

export default MemoizedAutocomplete;